import type { LucideIcon } from "lucide-react";

import type { ListKey } from "@/lib/clinic-grants";
import {
  CalendarDays,
  FolderOpen,
  IdCard,
  LayoutList,
  Receipt,
  Stethoscope,
  Wallet,
} from "lucide-react";

/**
 * The sections of the consolidated patient page (SPEC-PAC-01 §4.3). They live
 * in `?secao=` instead of nested routes: `Pagination` builds its links from the
 * query string, and a nested route per section would duplicate the `view
 * patient` guard seven times.
 */
export type PatientSection =
  | "resumo"
  | "odontologia"
  | "prontuario"
  | "agenda"
  | "financeiro"
  | "orcamentos"
  | "documentos"
  | "cadastro";

export type PatientSectionDefinition = {
  id: PatientSection;
  label: string;
  icon: LucideIcon;
};

export const SECTIONS: PatientSectionDefinition[] = [
  { id: "resumo", label: "Resumo", icon: LayoutList },
  { id: "odontologia", label: "Odontologia", icon: Stethoscope },
  { id: "prontuario", label: "Prontuário", icon: Stethoscope },
  { id: "agenda", label: "Agenda", icon: CalendarDays },
  { id: "financeiro", label: "Financeiro", icon: Wallet },
  { id: "orcamentos", label: "Orçamentos", icon: Receipt },
  { id: "documentos", label: "Documentos", icon: FolderOpen },
  { id: "cadastro", label: "Cadastro", icon: IdCard },
];

/**
 * SEC-GATES-02: a section that lists another module's records needs that
 * module's list grant; the rail hides it and a direct `?secao=` says so.
 */
const SECTION_GRANTS: Partial<Record<PatientSection, { list: ListKey; subject: string }>> = {
  orcamentos: { list: "budgets", subject: "os orçamentos" },
};

export function patientSectionGrant(section: PatientSection) {
  return SECTION_GRANTS[section];
}

/** The query parameter that selects a section. */
export const SECTION_PARAM = "secao";

/** Reception needs the patient's state before the clinical content. */
export const DEFAULT_SECTION: PatientSection = "resumo";

export function isPatientSection(value: string | null): value is PatientSection {
  return SECTIONS.some((section) => section.id === value);
}

export function patientSectionLabel(section: PatientSection): string {
  return SECTIONS.find((item) => item.id === section)?.label ?? section;
}

export function patientSectionUrl(patientId: string, section: PatientSection): string {
  return `/patients/${patientId}?${SECTION_PARAM}=${section}`;
}

export type ResolvedSection = {
  section: PatientSection;
  /** True when `?secao=` carried a value no section answers to. */
  unknownSection: boolean;
};

/**
 * An unknown `?secao=` falls back to the summary and says so on screen — the
 * repository forbids a silent fallback (PRODUCT_GOVERNANCE rule 8).
 */
export function resolvePatientSection(raw: string | null): ResolvedSection {
  if (raw === null || raw === "") return { section: DEFAULT_SECTION, unknownSection: false };
  if (isPatientSection(raw)) return { section: raw, unknownSection: false };

  return { section: DEFAULT_SECTION, unknownSection: true };
}

/**
 * The counters the rail can state today. They are summed from
 * `GET /patients/{id}/clinical-record`; Agenda, Financeiro and Orçamentos have
 * no server-side count yet and therefore show no number at all — a `0` from an
 * unavailable figure would be a lie (SPEC-PAC-01 §4.4).
 */
const SECTION_COUNTER_KEYS: Partial<Record<PatientSection, readonly string[]>> = {
  prontuario: ["anamneses", "evolutions"],
  documentos: ["documents", "exams", "certificates", "medications"],
};

export function sectionHasCounter(section: PatientSection): boolean {
  return SECTION_COUNTER_KEYS[section] !== undefined;
}

/** The section's counter, or `null` when the number is not known. */
export function patientSectionCount(
  section: PatientSection,
  summary: Record<string, number> | undefined
): number | null {
  const keys = SECTION_COUNTER_KEYS[section];
  if (!keys || !summary) return null;

  let total = 0;
  for (const key of keys) {
    const value = summary[key];
    if (typeof value !== "number" || !Number.isFinite(value)) return null;
    total += value;
  }

  return total;
}
